import { gql } from '@apollo/client'
import { sortReplies } from '@btime/helpers'
import React from 'react'

import { serviceOrdersClient } from '../../../../utils/apollo/api'
import { usePermalinkToken } from '../../../../utils/session/permalinkToken'
import { useQuery } from '../../../../utils/apollo/_helpers/useQuery'
import StatePresenter from '../../../../components/helpers/StatePresenter'

/**
 * Loader canônico do ServiceOrder para o PGR Bradesco — mesmo formato usado por todos
 * os PDFs customizados (ver bioqualynet/_load/ServiceOrderLoader.js): no-cache,
 * includeDeleted/ignoreSegments (comportamento herdado do pipeline existente,
 * preservado conforme pedido explícito da spec), permalinkToken para render público.
 *
 * `question.displayConditions` adicionado (2026-09-01) em preparação para a investigação
 * do pareamento Setor↔Cargo (seção 5.3, ver `pgr/sections/05.MetodologiaSection.js`):
 * Julia Buzzo (PM) já descartou (a) uma tabela fixa Setor->Cargo e (b) as duas perguntas
 * compartilharem o mesmo bloco repetível (blockIndex). A hipótese ainda não testada é que
 * cada uma das 20 perguntas de Cargo (`CARGO_QUESTION_IDS`) tenha um `displayConditions`
 * do tipo `within_refs` referenciando uma opção específica da pergunta de Setor (256) —
 * mecanismo padrão do checklist builder para "pergunta filha só aparece quando a pai tem
 * esta opção marcada" (ver `_reversa_sdd/checklists/design.md`, seção `within_refs`/
 * `formatChildDisplayConditions`). Esse campo NÃO era selecionado antes (só
 * `parentReference`/`reference`); sem ele, não há como testar essa hipótese mesmo com uma
 * atividade real em mãos. Nenhuma lógica nova usa este campo ainda — puramente
 * preparatório, sem mudança de comportamento.
 */
const Context = React.createContext([])
const { Provider } = Context
export const useServiceOrderState = () => React.useContext(Context)
export const useServiceOrder = () => useServiceOrderState()?.serviceOrder
export const useReplies = () => {
  const replies = useServiceOrder()?.replies || []
  return sortReplies(replies)
}

const QUERY = gql`
  query ServiceOrder($id: ID, $permalinkToken: String) {
    serviceOrder(id: $id, permalinkToken: $permalinkToken, filter: { includeDeleted: true, ignoreSegments: true }) {
      id
      yearlyId
      startDate
      endDate
      deleted
      insertedAt
      updatedAt
      address {
        search
        complement
      }
      place {
        id
        name
        resume
        placeLevelsResume
        fieldValues {
          id
          value
          field {
            id
            name
            type
          }
        }
      }
      company {
        id
        name
        nationalRegistration
      }
      user {
        id
        name
      }
      checklist {
        id
        name
      }
      replies {
        id
        value
        notes
        questionId
        blockIndex
        insertedAt
        isNonCompliance
        question {
          id
          name
          parentReference
          reference
          ordination
          categoryOrdination
          displayConditions
          type {
            id
            name
          }
          category {
            id
            name
          }
        }
      }
    }
  }
`

const ServiceOrderLoader = ({ children, id }) => {
  const permalinkToken = usePermalinkToken()

  const { data, ...state } = useQuery(QUERY, {
    variables: { id, permalinkToken },
    client: serviceOrdersClient
  })

  return (
    <StatePresenter {...state} empty={!data?.serviceOrder}>
      <Provider value={data}>{children}</Provider>
    </StatePresenter>
  )
}

export default ServiceOrderLoader
